import { useCallback } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { readCampaignSearch, type CampaignSearch } from './search';

export interface CampaignSearchWrite {
  replace?: boolean;
  with?: Partial<CampaignSearch>;
}

export type SetCampaignSearchParam<K extends keyof CampaignSearch> = (
  next: CampaignSearch[K],
  write?: CampaignSearchWrite,
) => void;

export function useCampaignSearchParam<K extends keyof CampaignSearch>(
  key: K,
  { replace }: { replace?: boolean } = {},
): [CampaignSearch[K], SetCampaignSearchParam<K>] {
  const navigate = useNavigate();
  const value = readCampaignSearch(useSearch({ strict: false }))[key];
  const setValue = useCallback<SetCampaignSearchParam<K>>(
    (next, write = {}) => {
      const patch: Partial<CampaignSearch> = { [key]: next, ...write.with };
      return navigate({
        to: '.',
        replace: write.replace ?? replace,
        search: (prev: Partial<CampaignSearch>) => ({ ...prev, ...patch }),
      });
    },
    [navigate, key, replace],
  );
  return [value, setValue];
}
